"use client";

import { c as _c } from "react/compiler-runtime";
import { AudioLines, ChevronUp, ExternalLink, Film, ImageIcon, LogOut, MessageSquare, PenTool, Users } from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useTheme } from "next-themes";
import { usePostHog } from "posthog-js/react";
import { useCallback } from "react";
import { CreditsDisplay } from "@/components/credits/credits-display";
import { ThemeToggle } from "@/components/landing/theme-toggle";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Logo } from "@/components/ui/logo";
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar";
import { useOrganization } from "@/hooks/useOrganization";
import { useUser } from "@/hooks/useUser";
import { clearLastUsedProjectCookiesAction } from "@/lib/actions/project";
import { useAuth } from "@/lib/auth-client";
import { useI18n } from "@llmgateway/i18n/react";
import { getChatUrl, getMarketingUrl } from "@llmgateway/shared/branding";
import { OrganizationSwitcher } from "./organization-switcher";
import { SidebarChatSearch, SidebarNewAction } from "./sidebar-actions";
import type { Organization } from "@/lib/types";
interface CanvasSidebarProps {
  organizations: Organization[];
  selectedOrganization: Organization | null;
  onSelectOrganization: (organization: Organization | null) => void;
  className?: string;
  onNewCanvas?: () => void;
}
export function CanvasSidebar(t0) {
  const $ = _c(249);
  const {
    organizations,
    selectedOrganization,
    onSelectOrganization,
    className,
    onNewCanvas
  } = t0;
  const {
    t
  } = useI18n();
  let switcherOrganizations;
  let t1;
  if ($[0] !== organizations || $[1] !== selectedOrganization?.id) {
    switcherOrganizations = organizations.filter(_temp);
    t1 = switcherOrganizations.find(org_0 => org_0.id === selectedOrganization?.id) ?? null;
    $[0] = organizations;
    $[1] = selectedOrganization?.id;
    $[2] = switcherOrganizations;
    $[3] = t1;
  } else {
    switcherOrganizations = $[2];
    t1 = $[3];
  }
  const switcherSelectedOrganization = t1;
  const router = useRouter();
  const posthog = usePostHog();
  const {
    user,
    isLoading: isUserLoading
  } = useUser();
  const {
    signOut
  } = useAuth();
  const {
    organization,
    isLoading: isOrgLoading
  } = useOrganization();
  let t2;
  if ($[4] !== posthog || $[5] !== router || $[6] !== signOut) {
    t2 = async () => {
      posthog.reset();
      try {
        await clearLastUsedProjectCookiesAction();
      } catch {}
      await signOut({
        fetchOptions: {
          onSuccess: () => {
            router.push(process.env.NODE_ENV === "development" ? "http://localhost:3003/login" : getChatUrl("/login"));
          }
        }
      });
    };
    $[4] = posthog;
    $[5] = router;
    $[6] = signOut;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const logout = t2;
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t3;
  if ($[8] !== searchParams) {
    t3 = searchParams.get("orgId");
    $[8] = searchParams;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const orgIdParam = t3;
  let t4;
  if ($[10] !== orgIdParam) {
    t4 = path => orgIdParam ? `${path}?orgId=${orgIdParam}` : path;
    $[10] = orgIdParam;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const withOrg = t4;
  const {
    theme,
    setTheme,
    systemTheme
  } = useTheme();
  const currentTheme = theme === "system" ? systemTheme : theme;
  let t5;
  if ($[12] !== currentTheme || $[13] !== setTheme) {
    t5 = () => {
      setTheme(currentTheme === "dark" ? "light" : "dark");
    };
    $[12] = currentTheme;
    $[13] = setTheme;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const toggleTheme = t5;
  const isAuthenticated = !!user;
  if (isUserLoading) {
    let t6;
    let t7;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <Logo className="h-10 w-10" />;
      t7 = <h1 className="text-xl font-semibold">doteb</h1>;
      $[15] = t6;
      $[16] = t7;
    } else {
      t6 = $[15];
      t7 = $[16];
    }
    let t8;
    if ($[17] !== t) {
      t8 = t("playground.canvas");
      $[17] = t;
      $[18] = t8;
    } else {
      t8 = $[18];
    }
    let t9;
    if ($[19] !== t8) {
      t9 = <SidebarHeader><div className="flex flex-col items-center gap-4 mb-4"><Link href="/" className="flex self-start items-center gap-2 my-2" prefetch={true}>{t6}{t7}<Badge>{t8}</Badge></Link></div></SidebarHeader>;
      $[19] = t8;
      $[20] = t9;
    } else {
      t9 = $[20];
    }
    let t10;
    if ($[21] !== className || $[22] !== t9) {
      t10 = <Sidebar className={className}>{t9}</Sidebar>;
      $[21] = className;
      $[22] = t9;
      $[23] = t10;
    } else {
      t10 = $[23];
    }
    return t10;
  }
  if (!isAuthenticated) {
    let t6;
    let t7;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <Logo className="h-10 w-10" />;
      t7 = <h1 className="text-xl font-semibold">doteb</h1>;
      $[24] = t6;
      $[25] = t7;
    } else {
      t6 = $[24];
      t7 = $[25];
    }
    let t8;
    if ($[26] !== t) {
      t8 = t("playground.canvas");
      $[26] = t;
      $[27] = t8;
    } else {
      t8 = $[27];
    }
    let t9;
    if ($[28] !== t8) {
      t9 = <Link href="/" className="flex self-start items-center gap-2 my-2" prefetch={true}>{t6}{t7}<Badge>{t8}</Badge></Link>;
      $[28] = t8;
      $[29] = t9;
    } else {
      t9 = $[29];
    }
    let t10;
    if ($[30] !== t) {
      t10 = t("playground.signInRequired");
      $[30] = t;
      $[31] = t10;
    } else {
      t10 = $[31];
    }
    let t11;
    if ($[32] !== t10) {
      t11 = <div className="font-medium mb-2">{t10}</div>;
      $[32] = t10;
      $[33] = t11;
    } else {
      t11 = $[33];
    }
    let t12;
    if ($[34] !== t) {
      t12 = t("playground.canvasSignInDescription");
      $[34] = t;
      $[35] = t12;
    } else {
      t12 = $[35];
    }
    let t13;
    if ($[36] !== t12) {
      t13 = <p className="text-muted-foreground mb-3">{t12}</p>;
      $[36] = t12;
      $[37] = t13;
    } else {
      t13 = $[37];
    }
    let t14;
    if ($[38] !== t) {
      t14 = t("auth.signIn");
      $[38] = t;
      $[39] = t14;
    } else {
      t14 = $[39];
    }
    let t15;
    if ($[40] !== t14) {
      t15 = <Button size="sm" asChild={true}><Link href="/login">{t14}</Link></Button>;
      $[40] = t14;
      $[41] = t15;
    } else {
      t15 = $[41];
    }
    let t16;
    if ($[42] !== t) {
      t16 = t("auth.createAccount");
      $[42] = t;
      $[43] = t16;
    } else {
      t16 = $[43];
    }
    let t17;
    if ($[44] !== t16) {
      t17 = <Button size="sm" variant="outline" asChild={true}><Link href="/signup">{t16}</Link></Button>;
      $[44] = t16;
      $[45] = t17;
    } else {
      t17 = $[45];
    }
    let t18;
    if ($[46] !== t15 || $[47] !== t17) {
      t18 = <div className="flex items-center justify-end gap-2">{t15}{t17}</div>;
      $[46] = t15;
      $[47] = t17;
      $[48] = t18;
    } else {
      t18 = $[48];
    }
    let t19;
    if ($[49] !== t11 || $[50] !== t13 || $[51] !== t18) {
      t19 = <div className="w-full rounded-md border p-4 text-sm">{t11}{t13}{t18}</div>;
      $[49] = t11;
      $[50] = t13;
      $[51] = t18;
      $[52] = t19;
    } else {
      t19 = $[52];
    }
    let t20;
    if ($[53] !== t19 || $[54] !== t9) {
      t20 = <SidebarHeader><div className="flex flex-col items-center gap-4 mb-4">{t9}{t19}</div></SidebarHeader>;
      $[53] = t19;
      $[54] = t9;
      $[55] = t20;
    } else {
      t20 = $[55];
    }
    let t21;
    if ($[56] !== className || $[57] !== t20) {
      t21 = <Sidebar className={className}>{t20}</Sidebar>;
      $[56] = className;
      $[57] = t20;
      $[58] = t21;
    } else {
      t21 = $[58];
    }
    return t21;
  }
  const t6 = (className ?? "") + " max-md:hidden";
  let t7;
  if ($[59] !== withOrg) {
    t7 = withOrg("/");
    $[59] = withOrg;
    $[60] = t7;
  } else {
    t7 = $[60];
  }
  let t8;
  let t9;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="flex aspect-square size-8 items-center justify-center"><Logo className="size-6" /></div>;
    t9 = <span className="text-lg font-bold tracking-tight">doteb</span>;
    $[61] = t8;
    $[62] = t9;
  } else {
    t8 = $[61];
    t9 = $[62];
  }
  let t10;
  if ($[63] !== t) {
    t10 = t("playground.chat");
    $[63] = t;
    $[64] = t10;
  } else {
    t10 = $[64];
  }
  let t11;
  if ($[65] !== t10) {
    t11 = <Badge variant="secondary" className="group-data-[collapsible=icon]:hidden">{t10}</Badge>;
    $[65] = t10;
    $[66] = t11;
  } else {
    t11 = $[66];
  }
  let t12;
  if ($[67] !== t11 || $[68] !== t7) {
    t12 = <SidebarMenuItem><SidebarMenuButton size="lg" asChild={true} tooltip="doteb"><Link href={t7} prefetch={true}>{t8}{t9}{t11}</Link></SidebarMenuButton></SidebarMenuItem>;
    $[67] = t11;
    $[68] = t7;
    $[69] = t12;
  } else {
    t12 = $[69];
  }
  let t13;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <SidebarChatSearch disabled={true} />;
    $[70] = t13;
  } else {
    t13 = $[70];
  }
  let t14;
  if ($[71] !== t) {
    t14 = t("playground.newCanvas");
    $[71] = t;
    $[72] = t14;
  } else {
    t14 = $[72];
  }
  let t15;
  if ($[73] !== onNewCanvas || $[74] !== t14) {
    t15 = <SidebarNewAction label={t14} onAction={onNewCanvas} />;
    $[73] = onNewCanvas;
    $[74] = t14;
    $[75] = t15;
  } else {
    t15 = $[75];
  }
  let t16;
  if ($[76] !== t) {
    t16 = t("playground.chat");
    $[76] = t;
    $[77] = t16;
  } else {
    t16 = $[77];
  }
  const t17 = pathname === "/";
  let t18;
  if ($[78] !== withOrg) {
    t18 = withOrg("/");
    $[78] = withOrg;
    $[79] = t18;
  } else {
    t18 = $[79];
  }
  let t19;
  if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <MessageSquare className="h-4 w-4" />;
    $[80] = t19;
  } else {
    t19 = $[80];
  }
  let t20;
  if ($[81] !== t) {
    t20 = t("playground.chat");
    $[81] = t;
    $[82] = t20;
  } else {
    t20 = $[82];
  }
  let t21;
  if ($[83] !== t20) {
    t21 = <span>{t20}</span>;
    $[83] = t20;
    $[84] = t21;
  } else {
    t21 = $[84];
  }
  let t22;
  if ($[85] !== t18 || $[86] !== t21) {
    t22 = <Link href={t18} prefetch={true}>{t19}{t21}</Link>;
    $[85] = t18;
    $[86] = t21;
    $[87] = t22;
  } else {
    t22 = $[87];
  }
  let t23;
  if ($[88] !== t16 || $[89] !== t17 || $[90] !== t22) {
    t23 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip={t16} isActive={t17}>{t22}</SidebarMenuButton></SidebarMenuItem>;
    $[88] = t16;
    $[89] = t17;
    $[90] = t22;
    $[91] = t23;
  } else {
    t23 = $[91];
  }
  let t24;
  if ($[92] !== t) {
    t24 = t("playground.groupChat");
    $[92] = t;
    $[93] = t24;
  } else {
    t24 = $[93];
  }
  const t25 = pathname === "/group";
  let t26;
  if ($[94] !== withOrg) {
    t26 = withOrg("/group");
    $[94] = withOrg;
    $[95] = t26;
  } else {
    t26 = $[95];
  }
  let t27;
  if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <Users className="h-4 w-4" />;
    $[96] = t27;
  } else {
    t27 = $[96];
  }
  let t28;
  if ($[97] !== t) {
    t28 = t("playground.groupChat");
    $[97] = t;
    $[98] = t28;
  } else {
    t28 = $[98];
  }
  let t29;
  if ($[99] !== t28) {
    t29 = <span>{t28}</span>;
    $[99] = t28;
    $[100] = t29;
  } else {
    t29 = $[100];
  }
  let t30;
  if ($[101] !== t26 || $[102] !== t29) {
    t30 = <Link href={t26} prefetch={true}>{t27}{t29}</Link>;
    $[101] = t26;
    $[102] = t29;
    $[103] = t30;
  } else {
    t30 = $[103];
  }
  let t31;
  if ($[104] !== t24 || $[105] !== t25 || $[106] !== t30) {
    t31 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip={t24} isActive={t25}>{t30}</SidebarMenuButton></SidebarMenuItem>;
    $[104] = t24;
    $[105] = t25;
    $[106] = t30;
    $[107] = t31;
  } else {
    t31 = $[107];
  }
  let t32;
  if ($[108] !== t) {
    t32 = t("playground.imageStudio");
    $[108] = t;
    $[109] = t32;
  } else {
    t32 = $[109];
  }
  const t33 = pathname === "/image";
  let t34;
  if ($[110] !== withOrg) {
    t34 = withOrg("/image");
    $[110] = withOrg;
    $[111] = t34;
  } else {
    t34 = $[111];
  }
  let t35;
  if ($[112] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <ImageIcon className="h-4 w-4" />;
    $[112] = t35;
  } else {
    t35 = $[112];
  }
  let t36;
  if ($[113] !== t) {
    t36 = t("playground.imageStudio");
    $[113] = t;
    $[114] = t36;
  } else {
    t36 = $[114];
  }
  let t37;
  if ($[115] !== t36) {
    t37 = <span>{t36}</span>;
    $[115] = t36;
    $[116] = t37;
  } else {
    t37 = $[116];
  }
  let t38;
  if ($[117] !== t34 || $[118] !== t37) {
    t38 = <Link href={t34} prefetch={true}>{t35}{t37}</Link>;
    $[117] = t34;
    $[118] = t37;
    $[119] = t38;
  } else {
    t38 = $[119];
  }
  let t39;
  if ($[120] !== t32 || $[121] !== t33 || $[122] !== t38) {
    t39 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip={t32} isActive={t33}>{t38}</SidebarMenuButton></SidebarMenuItem>;
    $[120] = t32;
    $[121] = t33;
    $[122] = t38;
    $[123] = t39;
  } else {
    t39 = $[123];
  }
  let t40;
  if ($[124] !== t) {
    t40 = t("playground.videoStudio");
    $[124] = t;
    $[125] = t40;
  } else {
    t40 = $[125];
  }
  const t41 = pathname === "/video";
  let t42;
  if ($[126] !== withOrg) {
    t42 = withOrg("/video");
    $[126] = withOrg;
    $[127] = t42;
  } else {
    t42 = $[127];
  }
  let t43;
  if ($[128] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = <Film className="h-4 w-4" />;
    $[128] = t43;
  } else {
    t43 = $[128];
  }
  let t44;
  if ($[129] !== t) {
    t44 = t("playground.videoStudio");
    $[129] = t;
    $[130] = t44;
  } else {
    t44 = $[130];
  }
  let t45;
  if ($[131] !== t44) {
    t45 = <span>{t44}</span>;
    $[131] = t44;
    $[132] = t45;
  } else {
    t45 = $[132];
  }
  let t46;
  if ($[133] !== t42 || $[134] !== t45) {
    t46 = <Link href={t42} prefetch={true}>{t43}{t45}</Link>;
    $[133] = t42;
    $[134] = t45;
    $[135] = t46;
  } else {
    t46 = $[135];
  }
  let t47;
  if ($[136] !== t40 || $[137] !== t41 || $[138] !== t46) {
    t47 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip={t40} isActive={t41}>{t46}</SidebarMenuButton></SidebarMenuItem>;
    $[136] = t40;
    $[137] = t41;
    $[138] = t46;
    $[139] = t47;
  } else {
    t47 = $[139];
  }
  let t48;
  if ($[140] !== t) {
    t48 = t("playground.audioStudio");
    $[140] = t;
    $[141] = t48;
  } else {
    t48 = $[141];
  }
  const t49 = pathname === "/audio";
  let t50;
  if ($[142] !== withOrg) {
    t50 = withOrg("/audio");
    $[142] = withOrg;
    $[143] = t50;
  } else {
    t50 = $[143];
  }
  let t51;
  if ($[144] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = <AudioLines className="h-4 w-4" />;
    $[144] = t51;
  } else {
    t51 = $[144];
  }
  let t52;
  if ($[145] !== t) {
    t52 = t("playground.audioStudio");
    $[145] = t;
    $[146] = t52;
  } else {
    t52 = $[146];
  }
  let t53;
  if ($[147] !== t52) {
    t53 = <span>{t52}</span>;
    $[147] = t52;
    $[148] = t53;
  } else {
    t53 = $[148];
  }
  let t54;
  if ($[149] !== t50 || $[150] !== t53) {
    t54 = <Link href={t50} prefetch={true}>{t51}{t53}</Link>;
    $[149] = t50;
    $[150] = t53;
    $[151] = t54;
  } else {
    t54 = $[151];
  }
  let t55;
  if ($[152] !== t48 || $[153] !== t49 || $[154] !== t54) {
    t55 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip={t48} isActive={t49}>{t54}</SidebarMenuButton></SidebarMenuItem>;
    $[152] = t48;
    $[153] = t49;
    $[154] = t54;
    $[155] = t55;
  } else {
    t55 = $[155];
  }
  let t56;
  if ($[156] !== t) {
    t56 = t("playground.canvas");
    $[156] = t;
    $[157] = t56;
  } else {
    t56 = $[157];
  }
  const t57 = pathname === "/canvas";
  let t58;
  if ($[158] !== withOrg) {
    t58 = withOrg("/canvas");
    $[158] = withOrg;
    $[159] = t58;
  } else {
    t58 = $[159];
  }
  let t59;
  if ($[160] === Symbol.for("react.memo_cache_sentinel")) {
    t59 = <PenTool className="h-4 w-4" />;
    $[160] = t59;
  } else {
    t59 = $[160];
  }
  let t60;
  if ($[161] !== t) {
    t60 = t("playground.canvas");
    $[161] = t;
    $[162] = t60;
  } else {
    t60 = $[162];
  }
  let t61;
  if ($[163] !== t60) {
    t61 = <span>{t60}</span>;
    $[163] = t60;
    $[164] = t61;
  } else {
    t61 = $[164];
  }
  let t62;
  if ($[165] !== t58 || $[166] !== t61) {
    t62 = <Link href={t58} prefetch={true}>{t59}{t61}</Link>;
    $[165] = t58;
    $[166] = t61;
    $[167] = t62;
  } else {
    t62 = $[167];
  }
  let t63;
  if ($[168] !== t56 || $[169] !== t57 || $[170] !== t62) {
    t63 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip={t56} isActive={t57}>{t62}</SidebarMenuButton></SidebarMenuItem>;
    $[168] = t56;
    $[169] = t57;
    $[170] = t62;
    $[171] = t63;
  } else {
    t63 = $[171];
  }
  let t64;
  if ($[172] !== t12 || $[173] !== t15 || $[174] !== t23 || $[175] !== t31 || $[176] !== t39 || $[177] !== t47 || $[178] !== t55 || $[179] !== t63) {
    t64 = <SidebarHeader><SidebarMenu>{t12}{t13}{t15}{t23}{t31}{t39}{t47}{t55}{t63}</SidebarMenu></SidebarHeader>;
    $[172] = t12;
    $[173] = t15;
    $[174] = t23;
    $[175] = t31;
    $[176] = t39;
    $[177] = t47;
    $[178] = t55;
    $[179] = t63;
    $[180] = t64;
  } else {
    t64 = $[180];
  }
  let t65;
  if ($[181] !== onSelectOrganization || $[182] !== switcherOrganizations || $[183] !== switcherSelectedOrganization) {
    t65 = switcherOrganizations.length > 0 ? <SidebarMenu className="group-data-[collapsible=icon]:hidden"><SidebarMenuItem><OrganizationSwitcher organizations={switcherOrganizations} selectedOrganization={switcherSelectedOrganization} onSelectOrganization={onSelectOrganization} /></SidebarMenuItem></SidebarMenu> : null;
    $[181] = onSelectOrganization;
    $[182] = switcherOrganizations;
    $[183] = switcherSelectedOrganization;
    $[184] = t65;
  } else {
    t65 = $[184];
  }
  let t66;
  if ($[185] !== t65) {
    t66 = <SidebarContent className="px-2 py-4">{t65}</SidebarContent>;
    $[185] = t65;
    $[186] = t66;
  } else {
    t66 = $[186];
  }
  const t67 = switcherSelectedOrganization ?? organization;
  const t68 = !switcherSelectedOrganization;
  let t69;
  if ($[187] !== isOrgLoading || $[188] !== t67 || $[189] !== t68) {
    t69 = <div className="group-data-[collapsible=icon]:hidden"><CreditsDisplay organization={t67} isLoading={isOrgLoading} isChatPlanOrg={t68} /></div>;
    $[187] = isOrgLoading;
    $[188] = t67;
    $[189] = t68;
    $[190] = t69;
  } else {
    t69 = $[190];
  }
  let t70;
  if ($[191] !== t || $[192] !== user?.name) {
    t70 = user?.name ?? t("playground.user");
    $[191] = t;
    $[192] = user?.name;
    $[193] = t70;
  } else {
    t70 = $[193];
  }
  let t71;
  if ($[194] !== user?.name) {
    t71 = user?.name?.split(" ").map(_temp2).join("").toUpperCase().slice(0, 2) ?? "U";
    $[194] = user?.name;
    $[195] = t71;
  } else {
    t71 = $[195];
  }
  let t72;
  if ($[196] !== t71) {
    t72 = <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground"><span className="text-xs font-semibold">{t71}</span></div>;
    $[196] = t71;
    $[197] = t72;
  } else {
    t72 = $[197];
  }
  const t73 = user?.name;
  let t74;
  if ($[198] !== t73) {
    t74 = <span className="truncate font-semibold">{t73}</span>;
    $[198] = t73;
    $[199] = t74;
  } else {
    t74 = $[199];
  }
  const t75 = user?.email;
  let t76;
  if ($[200] !== t75) {
    t76 = <span className="truncate text-xs text-muted-foreground">{t75}</span>;
    $[200] = t75;
    $[201] = t76;
  } else {
    t76 = $[201];
  }
  let t77;
  if ($[202] !== t74 || $[203] !== t76) {
    t77 = <div className="grid flex-1 text-left text-sm leading-tight">{t74}{t76}</div>;
    $[202] = t74;
    $[203] = t76;
    $[204] = t77;
  } else {
    t77 = $[204];
  }
  let t78;
  if ($[205] === Symbol.for("react.memo_cache_sentinel")) {
    t78 = <ChevronUp className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />;
    $[205] = t78;
  } else {
    t78 = $[205];
  }
  let t79;
  if ($[206] !== t70 || $[207] !== t72 || $[208] !== t77) {
    t79 = <DropdownMenuTrigger asChild={true}><SidebarMenuButton size="lg" tooltip={t70} className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">{t72}{t77}{t78}</SidebarMenuButton></DropdownMenuTrigger>;
    $[206] = t70;
    $[207] = t72;
    $[208] = t77;
    $[209] = t79;
  } else {
    t79 = $[209];
  }
  let t80;
  if ($[210] === Symbol.for("react.memo_cache_sentinel")) {
    t80 = process.env.NODE_ENV === "development" ? "http://localhost:3002/dashboard" : getMarketingUrl("/dashboard");
    $[210] = t80;
  } else {
    t80 = $[210];
  }
  let t81;
  if ($[211] === Symbol.for("react.memo_cache_sentinel")) {
    t81 = <ExternalLink className="mr-2 h-4 w-4" />;
    $[211] = t81;
  } else {
    t81 = $[211];
  }
  let t82;
  if ($[212] !== t) {
    t82 = t("playground.dashboard");
    $[212] = t;
    $[213] = t82;
  } else {
    t82 = $[213];
  }
  let t83;
  if ($[214] !== t82) {
    t83 = <DropdownMenuItem asChild={true}><a href={t80} target="_blank" rel="noopener noreferrer">{t81}{t82}</a></DropdownMenuItem>;
    $[214] = t82;
    $[215] = t83;
  } else {
    t83 = $[215];
  }
  let t84;
  if ($[216] === Symbol.for("react.memo_cache_sentinel")) {
    t84 = <DropdownMenuSeparator />;
    $[216] = t84;
  } else {
    t84 = $[216];
  }
  let t85;
  if ($[217] !== toggleTheme) {
    t85 = event => {
      event.preventDefault();
      toggleTheme();
    };
    $[217] = toggleTheme;
    $[218] = t85;
  } else {
    t85 = $[218];
  }
  let t86;
  if ($[219] !== t) {
    t86 = t("playground.theme");
    $[219] = t;
    $[220] = t86;
  } else {
    t86 = $[220];
  }
  let t87;
  if ($[221] !== t86) {
    t87 = <span>{t86}</span>;
    $[221] = t86;
    $[222] = t87;
  } else {
    t87 = $[222];
  }
  let t88;
  if ($[223] === Symbol.for("react.memo_cache_sentinel")) {
    t88 = <div onClick={_temp3} onKeyDown={_temp4}><ThemeToggle className="shrink-0" size="compact" /></div>;
    $[223] = t88;
  } else {
    t88 = $[223];
  }
  let t89;
  if ($[224] !== t85 || $[225] !== t87) {
    t89 = <DropdownMenuItem className="justify-between gap-3" onSelect={t85}>{t87}{t88}</DropdownMenuItem>;
    $[224] = t85;
    $[225] = t87;
    $[226] = t89;
  } else {
    t89 = $[226];
  }
  let t90;
  if ($[227] === Symbol.for("react.memo_cache_sentinel")) {
    t90 = <DropdownMenuSeparator />;
    $[227] = t90;
  } else {
    t90 = $[227];
  }
  let t91;
  if ($[228] === Symbol.for("react.memo_cache_sentinel")) {
    t91 = <LogOut className="mr-2 h-4 w-4" />;
    $[228] = t91;
  } else {
    t91 = $[228];
  }
  let t92;
  if ($[229] !== t) {
    t92 = t("playground.logOut");
    $[229] = t;
    $[230] = t92;
  } else {
    t92 = $[230];
  }
  let t93;
  if ($[231] !== logout || $[232] !== t92) {
    t93 = <DropdownMenuItem onClick={logout}>{t91}{t92}</DropdownMenuItem>;
    $[231] = logout;
    $[232] = t92;
    $[233] = t93;
  } else {
    t93 = $[233];
  }
  let t94;
  if ($[234] !== t83 || $[235] !== t89 || $[236] !== t93) {
    t94 = <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg" side="top" align="end" sideOffset={4}>{t83}{t84}{t89}{t90}{t93}</DropdownMenuContent>;
    $[234] = t83;
    $[235] = t89;
    $[236] = t93;
    $[237] = t94;
  } else {
    t94 = $[237];
  }
  let t95;
  if ($[238] !== t79 || $[239] !== t94) {
    t95 = <SidebarMenu><SidebarMenuItem><DropdownMenu>{t79}{t94}</DropdownMenu></SidebarMenuItem></SidebarMenu>;
    $[238] = t79;
    $[239] = t94;
    $[240] = t95;
  } else {
    t95 = $[240];
  }
  let t96;
  if ($[241] !== t69 || $[242] !== t95) {
    t96 = <SidebarFooter>{t69}{t95}</SidebarFooter>;
    $[241] = t69;
    $[242] = t95;
    $[243] = t96;
  } else {
    t96 = $[243];
  }
  let t97;
  if ($[244] !== t6 || $[245] !== t64 || $[246] !== t66 || $[247] !== t96) {
    t97 = <Sidebar collapsible="icon" className={t6}>{t64}{t66}{t96}</Sidebar>;
    $[244] = t6;
    $[245] = t64;
    $[246] = t66;
    $[247] = t96;
    $[248] = t97;
  } else {
    t97 = $[248];
  }
  return t97;
}
function _temp4(event_1) {
  return event_1.stopPropagation();
}
function _temp3(event_0) {
  return event_0.stopPropagation();
}
function _temp2(n) {
  return n[0];
}
function _temp(org) {
  return !org.isPersonal && !org.isChat;
}